// src/systems/combat/calculator.ts — Cálculo centralizado de daño (Fase 4)
import type { AsteriaMove, AsteriaSpecies } from '../../entities/asteria.js';
import { affinityMultiplier } from '../../data/affinities.js';
import { COMBAT_CONFIG } from '../../data/gameConfig.js';
import { getWeather } from '../../data/weather.js';
import { getTerrain } from '../../data/terrain.js';
import type { BattleUnit, BattleField } from './engine.js';

export type DamageResult = {
  damage: number;
  isCrit: boolean;
  affinity: number;
  affinityLabel: string;
  stab: number;
};

export function getStageMultiplier(stage:number): number {
  const table: Record<number, number> = {
    '-6': 0.25, '-5':0.28, '-4':0.33, '-3':0.4, '-2':0.5, '-1':0.66,
    '0':1, '1':1.5, '2':2, '3':2.5, '4':3, '5':3.5, '6':4
  };
  return table[stage] ?? 1;
}

export function calculateDamage(
  attacker: BattleUnit,
  defender: BattleUnit,
  move: AsteriaMove,
  field: BattleField,
  options?: { isCritOverride?: boolean }
): DamageResult {
  if(move.power===0) return { damage:0, isCrit:false, affinity:1, affinityLabel:'', stab:1 };
  const atkStage = getStageMultiplier(attacker.statStages.atk);
  const defStage = getStageMultiplier(defender.statStages.def);
  let atk = attacker.atk * atkStage;
  let def = defender.def * defStage;

  // Ability modifiers (simplificado)
  if(attacker.abilityId==='rafaga' && attacker.hp < attacker.maxHp*0.5) atk *= 1.1;

  const levelFactor = (2 * attacker.level / 5 + 2);
  let base = ((levelFactor * move.power * (atk / Math.max(1, def)) ) / 50) + 2;

  // STAB
  const stab = attacker.species.element === move.element ? 1.2 : 1;
  base *= stab;

  // Afinidad
  const affinity = affinityMultiplier(move.element, defender.species.element);
  base *= affinity;

  // Clima
  const weather = getWeather(field.weather.id);
  if(weather?.effects.element === move.element) base *= (weather.effects.damageMult ?? 1);
  if(move.element==='aqua' && field.weather.id==='sequia') base *= 0.5;
  if(move.element==='ignis' && field.weather.id==='lluvia') base *= 0.5;

  // Terreno
  const terrain = getTerrain(field.terrain.id);
  if(terrain?.effects.element === move.element) base *= (terrain.effects.damageMult ?? 1);

  // Crítico
  const isCrit = options?.isCritOverride ?? (Math.random() < COMBAT_CONFIG.critChance);
  if(isCrit) base *= COMBAT_CONFIG.critMultiplier;

  // Variación 0.85-1.0
  base *= (0.85 + Math.random()*0.15);

  const dmg = Math.max(1, Math.floor(base));
  let label = '';
  if(affinity >= 2) label='¡Muy eficaz!';
  else if(affinity >= 1.5) label='¡Eficaz!';
  else if(affinity <= 0.5) label='Poco eficaz...';
  else if(affinity < 1) label='Resistido...';
  if(isCrit) label = (label? label+' ':'') + '¡Golpe crítico!';

  return { damage: dmg, isCrit, affinity, affinityLabel: label, stab };
}

export function xpToNext(level:number): number {
  return Math.floor(20 * Math.pow(COMBAT_CONFIG.xpGrowth, level-1));
}

export function xpRewardForVictory(enemyLevel:number, isTrainer:boolean): number {
  const base = enemyLevel * 8 + 12;
  return isTrainer ? Math.floor(base * 1.4) : base;
}

export function aureosReward(enemyLevel:number, rarity:string, isTrainer:boolean): number {
  if(isTrainer) return 30 + enemyLevel*6;
  const mult = rarity==='rara'? 1.5 : rarity==='poco_comun'? 1.2 : 1;
  return Math.floor((12 + enemyLevel*3)*mult);
}
